import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  UserEntity,
  AdminRole,
  UserType,
  useLazyFetchUserProfileQuery,
} from 'src/store/userApi';
import { selectUser } from 'src/store/userSlice';
import { useCreateTestUserMutation } from 'src/store/matchmakerChatApi';
import {
  Simulation,
  SimulationEvent,
  StartSimulationDto,
  useDeleteSimulationMutation,
  useLazyGetSimulationQuery,
  useLazyListSimulationsQuery,
  useStartSimulationMutation,
  useStopSimulationMutation,
} from 'src/store/simulationApi';
import {
  SocketEvent,
  subscribeEvent,
  unsubscribeEvent,
} from 'src/store/socket';
import { useToast } from 'src/ui/Toast';
import MatchmakerChat from './matchmakerChat';
import PromptPanel from './promptPanel';
import SentPromptPanel from './sentPromptPanel';
import UserPickerModal from './userPickerModal';
import SimulateModal from './simulateModal';
import SimulationBar from './simulationBar';

/** Simulation copies are TEST users with a `sim+` email. */
const isSimulationCopy = (user: UserEntity | null) =>
  user?.userType === UserType.TEST && !!user.email?.startsWith('sim+');

/** Fallback refresh while a run is going, in case socket events are missed. */
const POLL_MS = 15000;

const MatchmakerChatPage = () => {
  const toast = useToast();
  const currentAdmin = useSelector(selectUser);
  const [impersonatedUser, setImpersonatedUser] = useState<UserEntity | null>(
    null
  );
  // key → versionId previewed in the chat (override, not activated).
  const [preview, setPreview] = useState<Record<string, number>>({});
  // Exact system prompt the backend sent on the last turn.
  const [sentPrompt, setSentPrompt] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Sent-prompt panel collapsed state (Area B).
  const [collapsed, setCollapsed] = useState(false);
  const [createTestUser, createStatus] = useCreateTestUserMutation();

  // Simulation state: the run shown above the chat, the user it copies (to go
  // back to on close), and live progress from the socket.
  const [simulateOpen, setSimulateOpen] = useState(false);
  const [simulation, setSimulation] = useState<Simulation | null>(null);
  const [simulationSource, setSimulationSource] = useState<UserEntity | null>(
    null
  );
  const [live, setLive] = useState<{ turn?: number; percentage?: number }>({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [startSimulation, startStatus] = useStartSimulationMutation();
  const [stopSimulation, stopStatus] = useStopSimulationMutation();
  const [deleteSimulation, deleteStatus] = useDeleteSimulationMutation();
  const [getSimulation] = useLazyGetSimulationQuery();
  const [listSimulations] = useLazyListSimulationsQuery();
  const [fetchUser] = useLazyFetchUserProfileQuery();

  const isAdmin = (currentAdmin?.adminRole ?? 0) >= AdminRole.ADMIN;
  const previewCount = Object.keys(preview).length;
  const running = simulation?.status === 'running';

  const errorToast = (title: string, error: any) =>
    toast({
      type: 'error',
      title,
      message: error?.data?.message || error?.message || error?.error,
    });

  const onTogglePreview = (key: string, versionId: number) => {
    setPreview((prev) => {
      const next = { ...prev };
      if (next[key] === versionId) delete next[key];
      else next[key] = versionId;
      return next;
    });
  };

  const onCreateTestUser = async () => {
    try {
      const user = await createTestUser({}).unwrap();
      setImpersonatedUser(user as unknown as UserEntity);
      toast({
        type: 'success',
        title: 'Test user created',
        message: (user as unknown as UserEntity).email,
      });
    } catch (error: any) {
      errorToast('Failed to create test user', error);
    }
  };

  // Picking a simulation copy (e.g. from the TEST filter) reopens its run;
  // picking anyone else closes the panel unless it is the run being shown.
  useEffect(() => {
    if (!impersonatedUser) return;
    if (simulation?.cloneUserId === impersonatedUser.id) return;
    if (!isSimulationCopy(impersonatedUser)) {
      setSimulation(null);
      return;
    }
    listSimulations({ cloneUserId: impersonatedUser.id })
      .unwrap()
      .then((rows) => {
        setLive({});
        setSimulation(rows[0] ?? null);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonatedUser?.id]);

  const reloadSimulation = async (id: string) => {
    try {
      setSimulation(await getSimulation(id).unwrap());
    } catch (error) {
      errorToast('Failed to load simulation', error);
    }
  };

  // Live progress: one ADMIN_LOG event per turn plus one at the end.
  useEffect(() => {
    if (!simulation || simulation.status !== 'running') return;
    const id = simulation.id;
    const onLog = (data: SimulationEvent) => {
      if (data?.type !== 'simulation' || data.simulationId !== id) return;
      setLive({ turn: data.turn, percentage: data.percentage });
      setRefreshKey((k) => k + 1);
      if (data.status !== 'running') reloadSimulation(id);
    };
    subscribeEvent(SocketEvent.ADMIN_LOG, onLog);
    const timer = setInterval(() => {
      setRefreshKey((k) => k + 1);
      reloadSimulation(id);
    }, POLL_MS);
    return () => {
      unsubscribeEvent(SocketEvent.ADMIN_LOG, onLog);
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulation?.id, simulation?.status]);

  const onStartSimulation = async (dto: StartSimulationDto) => {
    try {
      const run = await startSimulation(dto).unwrap();
      const copy = await fetchUser(run.cloneUserId).unwrap();
      setSimulationSource(impersonatedUser);
      setLive({});
      setSimulation(run);
      setImpersonatedUser(copy);
      setSimulateOpen(false);
    } catch (error) {
      errorToast('Failed to start simulation', error);
    }
  };

  const onStop = async () => {
    if (!simulation) return;
    try {
      setSimulation(await stopSimulation(simulation.id).unwrap());
    } catch (error) {
      errorToast('Failed to stop simulation', error);
    }
  };

  const onClose = () => {
    setSimulation(null);
    if (simulationSource) setImpersonatedUser(simulationSource);
    setSimulationSource(null);
  };

  const onDelete = async () => {
    if (!simulation) return;
    if (
      !window.confirm(
        'Delete this simulation and its test copy (user, profile, photos, conversation)?'
      )
    ) {
      return;
    }
    try {
      await deleteSimulation(simulation.id).unwrap();
      toast({ type: 'success', title: 'Simulation deleted' });
      setSimulation(null);
      setImpersonatedUser(simulationSource);
      setSimulationSource(null);
    } catch (error) {
      errorToast('Failed to delete simulation', error);
    }
  };

  const copy = isSimulationCopy(impersonatedUser);

  return (
    <div style={{ height: '100%', padding: 16, overflow: 'hidden' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: collapsed
            ? '420px minmax(0,1fr) 46px'
            : '420px minmax(0,1fr) 360px',
          gap: 16,
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Area A — Prompt editor (420px fixed) */}
        <div style={{ overflow: 'hidden', display: 'flex' }}>
          <PromptPanel preview={preview} onTogglePreview={onTogglePreview} />
        </div>

        {/* Area C — Chat (flex middle) — alerts + chat panel live inside */}
        <div style={{ overflow: 'hidden', display: 'flex' }}>
          <MatchmakerChat
            impersonatedUser={impersonatedUser}
            promptOverrides={preview}
            onSystemPrompt={setSentPrompt}
            onPickUser={() => setPickerOpen(true)}
            onCreateTestUser={onCreateTestUser}
            createStatus={createStatus}
            isAdmin={isAdmin}
            previewCount={previewCount}
            onSimulate={copy ? undefined : () => setSimulateOpen(true)}
            refreshKey={refreshKey}
            isSimulationCopy={copy}
            simulationRunning={running}
            simulationPanel={
              simulation && (
                <SimulationBar
                  simulation={simulation}
                  liveTurn={live.turn}
                  livePercentage={live.percentage}
                  stopping={stopStatus.isLoading}
                  deleting={deleteStatus.isLoading}
                  onStop={onStop}
                  onDelete={onDelete}
                  onClose={onClose}
                />
              )
            }
          />
        </div>

        {/* Area B — Sent prompt (360px, collapsible → 46px rail) */}
        <div style={{ overflow: 'hidden', display: 'flex' }}>
          <SentPromptPanel
            systemPrompt={sentPrompt}
            previewCount={previewCount}
            collapsed={collapsed}
            onToggle={() => setCollapsed((c) => !c)}
          />
        </div>
      </div>

      <UserPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedUserId={impersonatedUser?.id}
        onUserSelect={setImpersonatedUser}
      />

      {impersonatedUser && !copy && (
        <SimulateModal
          open={simulateOpen}
          onClose={() => setSimulateOpen(false)}
          sourceName={impersonatedUser.profile?.name || impersonatedUser.email}
          sourceUserId={impersonatedUser.id}
          defaultLang={impersonatedUser.language === 'en' ? 'en' : 'cn'}
          previewCount={previewCount}
          promptOverrides={preview}
          loading={startStatus.isLoading}
          onStart={onStartSimulation}
        />
      )}
    </div>
  );
};

export default MatchmakerChatPage;
